import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { ICustomerRole, ICustomerRoleRequest } from "../models/customerRole/ICustomerRole";
import { DefaultResponseWithData } from "../models/system";
import apiConfig from "../utils/apiConfig";

export default function useCustomerRoles(componentName = "useCustomerRoles") {
  const { get, post, put, del } = useApiHelper(apiConfig(componentName));
  const queryClient = useQueryClient();

  const roles = useQuery({
    queryKey: ["customerRoles"],
    queryFn: () =>
      get<DefaultResponseWithData<ICustomerRole[]>>("/customerrole"),
    staleTime: 1000 * 60 * 5,
  });

  const create = useMutation({
    mutationFn: (body: ICustomerRoleRequest) =>
      post<ICustomerRoleRequest, DefaultResponseWithData<ICustomerRole>>(
        "/customerrole",
        body
      ),
    onSettled: () => queryClient.invalidateQueries(["customerRoles"]),
  });

  const update = useMutation({
    mutationFn: (data: { id: string; body: ICustomerRoleRequest }) =>
      put<ICustomerRoleRequest, DefaultResponseWithData<ICustomerRole>>(
        `/customerrole/${data.id}`,
        data.body
      ),
    onSettled: () => {
      queryClient.invalidateQueries(["customerRoles"]);
      // Assigned staff claims are synced server-side; refresh user list.
      queryClient.invalidateQueries(["wilmaUsers"]);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => del(`/customerrole/${id}`),
    onSettled: () => queryClient.invalidateQueries(["customerRoles"]),
  });

  return {
    roles,
    create,
    update,
    remove,
  };
}
